'use client'

import { FALLBACK_PROFILE_PIC } from '@/app/constants/common'
import { BASE_URL, userProfileRoute } from '@/app/utils/routes'
import { CoverImageProjectFragment } from '@/gql/generated/graphql'
import { cn } from '@/lib/utils'
import { Button, buttonVariants } from '@components/button'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { buildShareProfileText } from '@utils/achievements'
import { buildTweetUrl } from '@utils/common'
import { useSelector } from '@xstate/store/react'
import { BodyText, DetailText, HeadlineText, ShareIcon } from 'design-configuration/components'
import { motion } from 'motion/react'
import { useSession } from 'next-auth/react'
import Image from 'next/image'
import Link from 'next/link'
import { useMemo } from 'react'
import { OnboardingStepFieldLabel } from './onboarding-step-field-label'
import { OnboardingStepHeader } from './onboarding-step-header'
import { onboardingStore } from './store'

export function ConfirmationStep({
  coverImageProjects
}: {
  coverImageProjects: CoverImageProjectFragment[]
}): React.ReactNode {
  const { data: session } = useSession()
  const publicAddress = session?.user?.publicAddress

  const profile = useSelector(onboardingStore, ({ context }) => {
    return context.profile
  })

  const tokens = useSelector(onboardingStore, ({ context }) => {
    return context.tokens
  })

  const userHasTokens = (tokens?.tokens_metadata_aggregate.aggregate?.count ?? 0) > 0

  const displayTokens = useMemo(() => {
    return [
      ...(tokens?.tokens_metadata ?? []),
      ...coverImageProjects.map((project) => ({
        ...(project.featured_token?.[0] ?? {}),
        project: { aspect_ratio: project.aspect_ratio }
      }))
    ].slice(0, 5)
  }, [tokens, coverImageProjects])

  const displayProfileImage = profile?.profile_picture?.url || FALLBACK_PROFILE_PIC

  return (
    <div className="gap-md flex min-h-full flex-col">
      <OnboardingStepHeader
        title="Your collector profile is ready!"
        subtitle="Here’s a snapshot of your collecting journey so far."
      />
      <div className="px-md gap-md flex flex-1 flex-col">
        <motion.div
          className="rounded-xs shadow-artwork overflow-hidden"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="h-xs" style={{ background: 'var(--blue-purp-gradient)' }} />
          <div className="px-sm pt-sm pb-md gap-sm flex flex-col md:flex-row md:items-center">
            <div className="gap-sm flex flex-1 items-center">
              <div className="border-outline-variant relative h-24 w-24 overflow-hidden rounded-full border">
                {typeof displayProfileImage === 'string' ? (
                  <ImageWithCustomLoader
                    src={profile?.profile_picture?.url ?? ''}
                    alt={`${profile?.name ?? 'User'} profile picture`}
                    className="object-cover"
                    fill
                    sizes="10vw"
                  />
                ) : (
                  <Image
                    src={displayProfileImage}
                    alt={`${profile?.name ?? 'User'} profile picture`}
                    className="object-cover"
                    fill
                    sizes="10vw"
                  />
                )}
              </div>
              <div>
                <HeadlineText className="font-medium">{profile?.name ?? 'User'}</HeadlineText>
                <BodyText className="text-on-surface-variant">{profile?.username ?? 'username'}</BodyText>
              </div>
            </div>
            <a
              href={buildTweetUrl(
                buildShareProfileText(profile?.user_achievements ?? []),
                BASE_URL + userProfileRoute(profile?.username ?? publicAddress ?? '')
              )}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(buttonVariants({ colorTheme: 'outline', size: 'small' }), 'self-start md:self-auto')}
            >
              <span className="gap-xs flex items-center">
                <ShareIcon className="fill-on-surface w-sm h-sm" />
                <span>Share profile</span>
              </span>
            </a>
          </div>
          <div className="px-md flex h-20 flex-col">
            <div className="flex justify-end">
              <DetailText className="text-on-surface-variant">
                Your{userHasTokens ? ' ' : ' future '}collection awaits
              </DetailText>
            </div>
            <div className="relative flex-1">
              <div className="py-xs gap-sm absolute left-0 right-0 top-0 flex h-28">
                {displayTokens.map((token) => (
                  <div key={token.media_url} className="relative flex-1">
                    <div className="relative w-full" style={{ aspectRatio: token.project?.aspect_ratio ?? 1 }}>
                      <ImageWithCustomLoader
                        src={token.media_url ?? ''}
                        alt={'Token image'}
                        fill
                        className="object-cover"
                        sizes="10vw"
                        style={{ filter: 'drop-shadow(3px 3px 6px rgba(0, 0, 0, 0.2))' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
        <div className="gap-md py-sm flex flex-col md:flex-row">
          <OnboardingStepFieldLabel
            label="Join the conversation"
            description="Connect with artists and collectors in our community"
            className="flex-1"
          />
          <a
            href="https://discord.com/invite/artblocks"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ colorTheme: 'outline', size: 'small' }), 'self-start md:self-auto')}
          >
            Join Art Blocks Discord
          </a>
        </div>
        <div className="gap-md py-sm flex flex-col md:flex-row">
          <OnboardingStepFieldLabel
            label="Start collecting amazing artwork now"
            description="Discover artworks under $100"
            className="flex-1"
          />
          <Link
            href={'/secondary-marketplace?priceRange=under-100'}
            className={cn(buttonVariants({ colorTheme: 'outline', size: 'small' }), 'self-start md:self-auto')}
            onClick={() => {
              onboardingStore.trigger.confirmationStepCompleted()
            }}
          >
            Browse collections
          </Link>
        </div>
      </div>
      <div className="p-md flex justify-between">
        <Button colorTheme="secondary" branded onClick={() => onboardingStore.trigger.back()}>
          Back
        </Button>
        <div className="gap-sm flex">
          <Link
            className={buttonVariants({ colorTheme: 'primary', branded: true })}
            href={userProfileRoute(profile?.username ?? publicAddress ?? '')}
            onClick={() => {
              onboardingStore.trigger.confirmationStepCompleted()
            }}
          >
            <span className="hidden font-[inherit] md:block">Continue to profile</span>
            <span className="block font-[inherit] md:hidden">Continue</span>
          </Link>
        </div>
      </div>
    </div>
  )
}
